import React, { useState, useEffect } from "react";
import {
  Card,
  CardMedia,
  Box,
  IconButton,
  LinearProgress,
  Typography,
  Chip,
  Button,
  Divider,
  Grid,
  Fade,
  Skeleton,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Share } from "@mui/icons-material";
import { MapPin, Bed, Square, Bath, Mail } from "lucide-react";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import { useMui, useSetting } from "../../hooks/Hooks";
import CryptoJS from "crypto-js";
import {
  shareGeneric,
  shareViaEmail,
  shareViaWhatsApp,
} from "../../components/FormUtility/FormUtility";
import {
  useGetProjectDetails,
  useGetProjectFiles,
} from "../../hooks/ProjectsDetails/UseProjectsDetails";
import { useAddPageView } from "../../hooks/PageViews/UsePageViewsHook";

// Reusable Stat Component
const PropertyStat = ({ icon: Icon, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Icon size={22} color="#64748b" />
    <Typography variant="h6">{value}</Typography>
  </Box>
);

// Reusable Detail Component
const DetailItem = ({ label, value, highlight }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" mb={0.5}>
      {label}
    </Typography>
    <Typography
      variant={highlight ? "h6" : "body1"}
      fontWeight={highlight ? "bold" : "medium"}
      color={highlight ? "#1976d2" : "inherit"}
    >
      {value}
    </Typography>
  </Box>
);

// Image Slider
export const ImageSlider = ({ images }) => {
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);

  const changeImage = (index) => {
    if (index === current) return;
    setLoading(true);
    setTimeout(() => {
      setCurrent(index);
      setLoading(false);
    }, 150);
  };

  const prev = () => changeImage((current - 1 + images.length) % images.length);
  const next = () => changeImage((current + 1) % images.length);

  return (
    <Box sx={{ position: "relative", height: 400 }}>
      {loading && (
        <LinearProgress
          sx={{ position: "absolute", top: 0, width: "100%", zIndex: 2 }}
        />
      )}
      <Fade in={!loading} timeout={300}>
        <CardMedia
          component="img"
          height="400"
          image={`${images[current]}?auto=format&fit=crop&w=1200&q=80`}
          alt={`Property image ${current + 1}`}
          sx={{ objectFit: "cover", borderRadius: 2 }}
        />
      </Fade>
      <IconButton onClick={prev} sx={navBtnStyle("left")}>
        <ChevronLeft />
      </IconButton>
      <IconButton onClick={next} sx={navBtnStyle("right")}>
        <ChevronRight />
      </IconButton>
      <Box sx={dotContainerStyle}>
        {images.map((_, i) => (
          <Box
            key={i}
            onClick={() => changeImage(i)}
            sx={{
              width: current === i ? 24 : 8,
              height: 8,
              borderRadius: 1,
              bgcolor: current === i ? "#fff" : "rgba(255,255,255,0.6)",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const navBtnStyle = (side) => ({
  position: "absolute",
  [side]: 16,
  top: "50%",
  transform: "translateY(-50%)",
  bgcolor: "rgba(255,255,255,0.9)",
  color: "#000",
  boxShadow: 2,
  "&:hover": { bgcolor: "#fff" },
});

const dotContainerStyle = {
  position: "absolute",
  bottom: 20,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 1,
};

// Loading Skeleton
const PropertyDetailsSkeleton = () => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3,
      overflow: "hidden",
      background: "#fff",
      border: "1px solid #e0e0e0",
      boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
      padding: 2,
      mt: 2,
    }}
  >
    <Skeleton variant="rectangular" height={400} />
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="rectangular" width={80} height={24} />
      </Box>
      <Skeleton variant="text" width="80%" height={20} mb={2} />
      <Box sx={{ display: "flex", gap: 4, mb: 2 }}>
        <Skeleton variant="text" width={60} height={24} />
        <Skeleton variant="text" width={60} height={24} />
        <Skeleton variant="text" width={60} height={24} />
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Skeleton variant="text" width="30%" height={24} mb={1} />
      <Skeleton variant="text" width="100%" height={20} mb={1} />
      <Skeleton variant="text" width="100%" height={20} mb={1} />
      <Skeleton variant="text" width="80%" height={20} mb={3} />
      <Divider sx={{ mb: 2 }} />
      <Skeleton variant="text" width="30%" height={24} mb={1} />
      <Grid container spacing={3}>
        {[...Array(6)].map((_, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Skeleton variant="text" width="80%" height={20} mb={1} />
            <Skeleton variant="text" width="60%" height={24} />
          </Grid>
        ))}
      </Grid>
    </Box>
  </Card>
);

export default function ProjectDetails({ dispatch }) {
  const { globalData } = useSetting();
  const { ArrowBackIosIcon } = useMui();
  const [propertyData, setPropertyData] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [decryptionError, setDecryptionError] = useState(null);

  let CryptoJSKey = globalData.CompanyName + "@" + globalData.Version;

  useEffect(() => {
    try {
      const encryptedData = localStorage.getItem("updateglobal_userdata");
      if (!encryptedData) {
        setDecryptionError("Project information not found");
        return;
      }

      const bytes = CryptoJS.AES.decrypt(encryptedData, CryptoJSKey);
      const decryptedText = bytes.toString(CryptoJS.enc.Utf8);

      if (!decryptedText) {
        setDecryptionError("Invalid or corrupted project ID");
        return;
      }

      const projectId = JSON.parse(decryptedText);
      setProjectId(projectId);
    } catch (err) {
      console.error("Decryption error:", err);
      setDecryptionError("Failed to decrypt project info");
    }
  }, [CryptoJSKey]);

  const { mutate: addPageView } = useAddPageView();
  useEffect(() => {
    if (projectId) {
      addPageView(projectId);
    }
  }, [projectId, addPageView]);

  const {
    data: projectDetails,
    isLoading: isLoadingDetails,
    error: errorDetails,
  } = useGetProjectDetails(projectId);
  console.log(projectDetails, "sndjhdkjhdkh");
  const {
    data: projectFiles,
    isLoading: isLoadingFiles,
    error: errorFiles,
  } = useGetProjectFiles(projectId, null);

  useEffect(() => {
    if (projectDetails && projectFiles) {
      const allFiles = projectFiles || [];
      const galleryFiles = allFiles.filter(
        (file) => file.file_type === "gallery"
      );
      const brochureFiles = allFiles.filter(
        (file) => file.file_type === "brochure"
      );
      const floorPlanFiles = allFiles.filter(
        (file) => file.file_type === "floor_plan"
      );

      const formattedData = {
        id: projectDetails.prj_id,
        name: projectDetails.prj_name,
        address: projectDetails.prj_address,
        code: `#${projectDetails.prj_id}`,
        location: `${projectDetails.prj_area_or_location || ""}, ${
          projectDetails.prj_city || ""
        }`,
        stats: {
          beds:
            projectDetails.prj_configurations &&
            projectDetails.prj_configurations.length > 0
              ? projectDetails.prj_configurations[0].category || "N/A"
              : "N/A",
          baths: projectDetails.prj_bathroom || "N/A",
          sqft: projectDetails.prj_area
            ? `${projectDetails.prj_area} ${projectDetails.prj_areaunit || ""}`
            : "N/A",
        },
        description:
          projectDetails.prj_description || "No description available",
        details: [
          { label: "Project Type", value: projectDetails.prj_type || "N/A" },
          {
            label: "Availability",
            value: projectDetails.prj_availability || "N/A",
          },
          {
            label: "Status",
            value: (
              <Chip
                label={projectDetails.prj_availability || "N/A"}
                size="small"
                sx={{
                  backgroundColor:
                    projectDetails.prj_availability === "Ready To Move"
                      ? "#e8f5e8"
                      : projectDetails.prj_availability === "Under Construction"
                      ? "#e3f2fd"
                      : projectDetails.prj_availability === "New Launch"
                      ? "#fff8e1"
                      : "#f5f5f5",
                  color:
                    projectDetails.prj_availability === "Ready To Move"
                      ? "#2e7d32"
                      : projectDetails.prj_availability === "Under Construction"
                      ? "#1976d2"
                      : projectDetails.prj_availability === "New Launch"
                      ? "#ff8f00"
                      : "#757575",
                }}
              />
            ),
          },
          {
            label: "Starting Price",
            value: projectDetails.prj_start_price
              ? `₹${projectDetails.prj_start_price} Cr`
              : "Price not set",
            highlight: true,
          },
          { label: "RERA Number", value: projectDetails.prj_rera || "N/A" },
          {
            label: "Possession",
            value:
              projectDetails.prj_poss_year && projectDetails.prj_poss_month
                ? `${projectDetails.prj_poss_month} ${projectDetails.prj_poss_year}`
                : "N/A",
          },
        ],
        images:
          galleryFiles.length > 0
            ? galleryFiles.map((file) => {
                const imageUrl = `${globalData.API_URL}/${file.file_path}`;
                // console.log("[PropertyDetails] Gallery image URL:", imageUrl);
                return imageUrl;
              })
            : ["https://www.dialurbanodisha.com/storage/no-property.jpg"],
        brochureUrl:
          brochureFiles.length > 0
            ? (() => {
                const brochureUrl = `${globalData.API_URL}/${brochureFiles[0].file_path}`;
                // console.log("[PropertyDetails] Brochure URL:", brochureUrl);
                return brochureUrl;
              })()
            : null,
        floorPlans: floorPlanFiles.map((file) => {
          const floorPlanUrl = `${globalData.API_URL}/${file.file_path}`;
          // console.log("[PropertyDetails] Floor plan URL:", floorPlanUrl);
          return {
            name: file.file_name,
            url: floorPlanUrl,
          };
        }),
        configurations: projectDetails.prj_configurations || [],
        wings: (() => {
          try {
            return projectDetails?.prj_wing_units
              ? JSON.parse(projectDetails.prj_wing_units)
              : [];
          } catch (e) {
            console.error("Error parsing wings JSON:", e);
            return [];
          }
        })(),
        amenities: projectDetails.prj_amenities || [],
        contact: {
          name: projectDetails.prj_person_name || "N/A",
          contactperson: projectDetails.prj_person_contact || "N/A",
          email: projectDetails.prj_email_id || "N/A",
        },
        towers: projectDetails.prj_towers || "N/A",
        floors: projectDetails.prj_t_floors || "N/A",
        totalAvailability: projectDetails.prj_t_availability || "N/A",
        specifications: {
          structure: projectDetails.prj_structure || "N/A",
          lift: projectDetails.prj_lift || "N/A",
          doorsAndWindows: projectDetails.prj_d_and_w || "N/A",
          bathroom: projectDetails.prj_bathroom || "N/A",
          lobby: projectDetails.prj_lobby || "N/A",
          flooring: projectDetails.prj_flooring || "N/A",
          wallFinish: projectDetails.prj_wall_finish || "N/A",
          kitchen: projectDetails.prj_kitchen_and_u || "N/A",
        },
        nearbyLocations: {
          railway: `${projectDetails.prj_railwayd || "N/A"} ${
            projectDetails.prj_railwayd_unit || ""
          }`,
          airport: `${projectDetails.prj_airportd || "N/A"} ${
            projectDetails.prj_airportd_unit || ""
          }`,
          hospital: `${projectDetails.prj_hospitald || "N/A"} ${
            projectDetails.prj_hospitald_unit || ""
          }`,
          market: `${projectDetails.prj_marketd || "N/A"} ${
            projectDetails.prj_marketd_unit || ""
          }`,
          school: `${projectDetails.prj_schoold || "N/A"} ${
            projectDetails.prj_schoold_unit || ""
          }`,
          bank: `${projectDetails.prj_bankd || "N/A"} ${
            projectDetails.prj_bankd_unit || ""
          }`,
          atm: `${projectDetails.prj_atmd || "N/A"} ${
            projectDetails.prj_atmd_unit || ""
          }`,
        },
        video: projectDetails.prj_video || null,
      };

      setPropertyData(formattedData);
      // console.log("[PropertyDetails] Property data set successfully");
    }
  }, [projectDetails, projectFiles]);

  console.log(propertyData, "propertyData");

  const handleEmailShare = () => {
    if (!propertyData) return;

    const price =
      propertyData.details.find((d) => d.label === "Starting Price")?.value ||
      "N/A";

    shareViaEmail({
      name: propertyData.name,
      location: propertyData.location,
      price,
    });
  };

  const handleWhatsAppShare = () => {
    if (!propertyData) return;

    const price =
      propertyData.details.find((d) => d.label === "Starting Price")?.value ||
      "N/A";

    shareViaWhatsApp({
      name: propertyData.name,
      location: propertyData.location,
      price,
    });
  };

  const handleShare = () => {
    if (!propertyData) return;

    shareGeneric({
      name: propertyData.name,
      location: propertyData.location,
    });
  };

  if (isLoadingDetails || isLoadingFiles) {
    // console.log("[PropertyDetails] Rendering loading skeleton");
    return (
      <>
        <Breadcrumb
          PageName="Property Details"
          BackButton={[
            true,
            "Back",
            <ArrowBackIosIcon />,
            () => dispatch({ event: "allprojects" }),
          ]}
        />
        <PropertyDetailsSkeleton />
      </>
    );
  }

  if (decryptionError || errorDetails || errorFiles) {
    const errorMessage =
      decryptionError ||
      errorDetails?.message ||
      errorFiles?.message ||
      "Failed to load project details";
    // console.log("[PropertyDetails] Rendering error state:", errorMessage);
    return (
      <>
        <Breadcrumb
          PageName="Property Details"
          BackButton={[
            true,
            "Back",
            <ArrowBackIosIcon />,
            () => dispatch({ event: "allprojects" }),
          ]}
        />
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            background: "#fff",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
            padding: 2,
            mt: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="error">
            Error loading property details
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {errorMessage}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Card>
      </>
    );
  }

  if (!propertyData) {
    // console.log("[PropertyDetails] Rendering no data state");
    return (
      <>
        <Breadcrumb
          PageName="Property Details"
          BackButton={[
            true,
            "Back",
            <ArrowBackIosIcon />,
            () => dispatch({ event: "allprojects" }),
          ]}
        />
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            background: "#fff",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
            padding: 2,
            mt: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h6">Property not found</Typography>
        </Card>
      </>
    );
  }

  // console.log("[PropertyDetails] Rendering property details with data");
  return (
    <>
      <Breadcrumb
        PageName="Property Details"
        BackButton={[
          true,
          "Back",
          <ArrowBackIosIcon />,
          () => dispatch({ event: "allprojects" }),
        ]}
      />
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: "#fff",
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
          padding: 2,
          mt: 2,
        }}
      >
        <ImageSlider images={propertyData.images} />
        <Box sx={{ py: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h5" fontWeight="bold">
                {propertyData.name}
              </Typography>
              <Chip
                label={propertyData.code}
                size="small"
                sx={{ backgroundColor: "#e3f2fd", color: "#1976d2" }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                startIcon={<Mail size={18} />}
                onClick={handleEmailShare}
                sx={{
                  color: "#565a5b",
                  padding: "6px 12px",
                  fontWeight: 600,
                  textTransform: "none",
                  letterSpacing: "0.5px",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                  },
                }}
              >
                Send Mail
              </Button>
              <Button
                startIcon={<WhatsAppIcon size={18} />}
                onClick={handleWhatsAppShare}
                sx={{
                  color: "#fff",
                  backgroundColor: "#128C7E",
                  padding: "6px 12px",
                  fontWeight: 600,
                  textTransform: "none",
                  letterSpacing: "0.5px",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  "&:hover": {
                    backgroundColor: "#0d6b60ff",
                  },
                }}
              >
                WhatsApp
              </Button>
              <Button
                startIcon={<Share size={18} />}
                onClick={handleShare}
                sx={{
                  color: "#565a5b",
                  padding: "6px 12px",
                  fontWeight: 600,
                  textTransform: "none",
                  letterSpacing: "0.5px",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                  },
                }}
              >
                Share
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <MapPin size={18} color="#64748b" />
            <Typography variant="body1" color="text.secondary">
              {propertyData.location}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 4, mt: 2 }}>
            <PropertyStat icon={Bed} value={propertyData.stats.beds} />
            <PropertyStat icon={Bath} value={propertyData.stats.baths} />
            <PropertyStat icon={Square} value={propertyData.stats.sqft} />
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ pb: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            About the Property
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            lineHeight={1.6}
            mb={3}
          >
            {propertyData.description}
          </Typography>
          {propertyData.brochureUrl && (
            <Box mb={3}>
              <Typography variant="h6" fontWeight="bold" mb={1}>
                Brochure
              </Typography>
              <Button
                variant="contained"
                href={propertyData.brochureUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mr: 2 }}
              >
                Download Brochure
              </Button>
            </Box>
          )}
          {propertyData.video && (
            <Box mb={3}>
              <Typography variant="h6" fontWeight="bold" mb={1}>
                Project Video
              </Typography>
              <Button
                variant="contained"
                href={propertyData.video}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mr: 2 }}
                onClick={() => {
                  console.log(
                    "[PropertyDetails] Video link clicked:",
                    propertyData.video
                  );
                }}
              >
                Watch Video
              </Button>
            </Box>
          )}
          {propertyData.floorPlans && propertyData.floorPlans.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" fontWeight="bold" mb={1}>
                Floor Plans
              </Typography>
              <Grid container spacing={2}>
                {propertyData.floorPlans.map((plan, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <CardMedia
                      component="img"
                      height="150"
                      image={plan.url}
                      alt={`Floor Plan ${index + 1}`}
                      sx={{
                        objectFit: "cover",
                        borderRadius: 1,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        console.log(
                          "[PropertyDetails] Floor plan clicked:",
                          plan.url
                        );
                        window.open(plan.url, "_blank");
                      }}
                    />
                    <Typography variant="body2" mt={1}>
                      {plan.name}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          {propertyData.configurations &&
            propertyData.configurations.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" fontWeight="bold" mb={1}>
                  Configurations
                </Typography>
                <Grid container spacing={2}>
                  {propertyData.configurations.map((config, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {config.category}
                        </Typography>
                        <Typography variant="body2">
                          {config.from_area} - {config.to_area} {config.aunit}
                        </Typography>
                        <Typography variant="body2">
                          Price: ₹{config.from_price} - ₹{config.to_price} Cr
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

          {/* ⭐ WINGS SECTION ⭐ */}
          {propertyData.wings && propertyData.wings.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" fontWeight="bold" mb={1}>
                Wings & Unit Availability
              </Typography>

              <Grid container spacing={2}>
                {propertyData.wings.map((wing, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{ mb: 1 }}
                      >
                        Wing {wing.wing}
                      </Typography>

                      {/* Units List */}
                      {wing.units && wing.units.length > 0 ? (
                        wing.units.map((unit, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                              p: 1,
                              borderRadius: 1,
                              backgroundColor:
                                unit.status === "available"
                                  ? "#e8f5e9"
                                  : unit.status === "sold"
                                  ? "#ffebee"
                                  : "#fff8e1",
                            }}
                          >
                            <Typography variant="body2">
                              Unit {unit.unit_no}
                            </Typography>

                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{
                                color:
                                  unit.status === "available"
                                    ? "#2e7d32"
                                    : unit.status === "sold"
                                    ? "#d32f2f"
                                    : "#ff8f00",
                                textTransform: "capitalize",
                              }}
                            >
                              {unit.status}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2">No units found</Typography>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {propertyData.amenities && propertyData.amenities.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" fontWeight="bold" mb={1}>
                Amenities
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {propertyData.amenities.map((amenity, index) => (
                  <Chip
                    key={index}
                    label={amenity}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}
          <Box mb={3}>
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Project Specifications
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Structure
                </Typography>
                <Typography variant="body1">
                  {propertyData.specifications.structure}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Lifts
                </Typography>
                <Typography variant="body1">
                  {propertyData.specifications.lift}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Doors and Windows
                </Typography>
                <Typography variant="body1">
                  {propertyData.specifications.doorsAndWindows}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Bathroom
                </Typography>
                <Typography variant="body1">
                  {propertyData.specifications.bathroom}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Lobby
                </Typography>
                <Typography variant="body1">
                  {propertyData.specifications.lobby}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Flooring
                </Typography>
                <Typography variant="body1">
                  {propertyData.specifications.flooring}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Wall Finish
                </Typography>
                <Typography variant="body1">
                  {propertyData.specifications.wallFinish}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Kitchen and Utility
                </Typography>
                <Typography variant="body1">
                  {propertyData.specifications.kitchen}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <Box mb={3}>
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Nearby Locations
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Railway Station
                </Typography>
                <Typography variant="body1">
                  {propertyData.nearbyLocations.railway}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Airport
                </Typography>
                <Typography variant="body1">
                  {propertyData.nearbyLocations.airport}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Hospital
                </Typography>
                <Typography variant="body1">
                  {propertyData.nearbyLocations.hospital}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Market
                </Typography>
                <Typography variant="body1">
                  {propertyData.nearbyLocations.market}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  School
                </Typography>
                <Typography variant="body1">
                  {propertyData.nearbyLocations.school}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Bank
                </Typography>
                <Typography variant="body1">
                  {propertyData.nearbyLocations.bank}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  ATM
                </Typography>
                <Typography variant="body1">
                  {propertyData.nearbyLocations.atm}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight="bold" mb={1}>
            Details
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            The property's essential info
          </Typography>
          <Grid container spacing={3}>
            {propertyData.details.map((detail, i) => (
              <Grid item xs={12} md={4} key={i}>
                <DetailItem {...detail} />
              </Grid>
            ))}
          </Grid>
          <Box mt={4}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Contact Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Contact Person
                </Typography>
                <Typography variant="body1">
                  {propertyData.contact.name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Contact Number
                </Typography>
                <Typography variant="body1">
                  {propertyData.contact.contactperson}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {propertyData.contact.email}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>
    </>
  );
}
